import zoneTracking from '@kissui/helpers/src/assets/js/zoneTracking'
import createProps from '@kissui/helpers/src/props.helpers'
import { makeId, stringifyForAttribute } from '@kissui/helpers/src/utils'

class CardArticle extends HTMLElement {
    connectedCallback() {
        this.props = createProps(this.attributes)
        this.classes = this.createClasses()
        this.randomIdVideo = `card_article_video_${makeId(5)}`
        this.render()
        this.initTracking()
    }

    createClasses() {
        const { layout } = this.props
        if (!layout) {
            return
        }

        const { contrast = 'light' } = layout
        const baseClass = 'nb-card-article'

        return {
            base: `${baseClass} ${baseClass}--${contrast}`,
            heading: `${baseClass}__heading h-lg-500`,
            description: `${baseClass}__description t-sm-400`,
            visual: `${baseClass}__visual`
        }
    }

    render() {
        const { copywriting } = this.props
        const { video_autoplay: { video_id = '' } = {} } = copywriting

        this.hasAutoPlayVideo = video_id !== ''

        if (!copywriting) {
            return
        }

        this.classes.base.split(' ').forEach(className => this.classList.add(className))
        this.innerHTML = this.renderInner()

        if (this.hasAutoPlayVideo) {
            this.querySelector(`#${this.randomIdVideo}`)?.addEventListener(
                'click',
                this.boundOnButtonSelfVideoClick
            )
        }
    }

    boundOnButtonSelfVideoClick(e) {
        const targetElement = e.target || e.srcElement
        const video = document.querySelector('.autoplay-video video')
        if (targetElement.dataset.status === 'pause') {
            video.pause()
            targetElement.dataset.status = 'play'
            targetElement.classList.remove('icon-pause')
            targetElement.classList.add('icon-play')
        } else {
            video.play()
            targetElement.dataset.status = 'pause'
            targetElement.classList.remove('icon-play')
            targetElement.classList.add('icon-pause')
        }
    }

    renderInner() {
        return `${this.renderCategory()}
                ${this.renderVisual()}
                ${this.renderHeading()}
                ${this.renderDescription()}
                ${this.renderLink()}`
    }

    renderHeading() {
        const { heading = '' } = this.props.copywriting
        const { headingLevel = '' } = this.props.copywriting

        if (!heading) {
            return ''
        }

        // Contextual change heading change based use within page
        return `<h${headingLevel} class="${this.classes.heading}">${heading}</h${headingLevel}>`
    }

    renderDescription() {
        const { description = '' } = this.props.copywriting

        if (!description) {
            return ''
        }

        return `<p class="${this.classes.description}">${description}</p>`
    }

    renderCategory() {
        const { category: { label = '', target = '', size = 'large' } = {} } =
            this.props.copywriting
        const { contrast } = this.props.layout

        if (!label || !target) {
            return ''
        }

        const color = contrast === 'dark' ? 'white' : 'black'
        return `
            <nb-link
                color="${color}"
                icon_right="24/symbol/arrow-right"
                link="${target}"
                size="${size}"
                title="${label}"
                cta_link_type="true"
            >${label}</nb-link>
        `
    }

    renderLink() {
        const { campaign } = this.props
        const { link } = this.props.copywriting
        const instanceIndex = !isNaN(this.props.instanceIndex)
            ? this.props.instanceIndex.toString()
            : ''

        if (!link || !link.label) {
            return ''
        }

        return `<nb-link
                custom_tracking="true"
                seo_label="${link.seo_label || ''}"
                tracking_label="${link.tracking_label || ''}"
                campaign_id="${campaign.id || ''}"
                campaign_name="${campaign.name || ''}"
                campaign_position="${campaign.position || ''}"
                campaign_creative="${campaign.creative || ''}"
                campaign_instance_index="${instanceIndex}"
                color="${link.color}"
                link="${link.link}"
                size="${link.size}"
                title="${link.label}"
            >${link.label}</nb-link>`
    }

    renderVisual() {
        const { visual = {}, video_autoplay = {} } = this.props.copywriting
        const { src = '', alt = '' } = visual
        const { video_id = '' } = video_autoplay

        if (video_id) {
            return `
                <div class="autoplay-container">
                    <nb-video class="autoplay-video" data="${stringifyForAttribute(
                        video_autoplay
                    )}"></nb-video>
                    <button id="${
                        this.randomIdVideo
                    }" class="autoplay-button video_button icon-pause" tabindex="0" aria-pressed="false" data-status="pause">
                        <span class="video_button__icon">
                            <nb-icon icon="24/symbol/play-solid" class="playButton"></nb-icon>
                            <nb-icon icon="24/symbol/pause-solid" class="pauseButton"></nb-icon>
                        </span>
                    </button>
                </div>
            `
        }

        if (!src) {
            return ''
        }

        return `<img loading=lazy class="${this.classes.visual}" src="${src}" alt="${alt}" />`
    }

    initTracking() {
        const { campaign } = this.props
        if (!campaign) {
            return
        }

        const { id, name, creative, position } = campaign

        zoneTracking(this, { id, name, position, creative })
    }

    disconnectedCallback() {
        this.querySelector(`#${this.randomIdVideo}`)?.removeEventListener(
            'click',
            this.boundOnButtonSelfVideoClick
        )
    }
}

customElements.get('nb-card-article') || customElements.define('nb-card-article', CardArticle)

export default CardArticle
